
import React from "react";
import Nav from "@/components/Nav";

const SECTIONS = [
  {
    title: "1. Data Fiduciary Identification & Overview",
    body: [
      <>
        Hacktonomy (
        <a
          href="https://hacktonomy.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#16fcfb] hover:underline"
        >
          hacktonomy.com
        </a>
        ) is a brand owned and operated by CyberFrat Private Limited
        ("CyberFrat", "Company", "we", "us", or "our"), a company incorporated
        under the Companies Act, 2013, having its registered office at
        [registered address, Mumbai].
      </>,
      "CyberFrat is the Data Fiduciary under the Digital Personal Data Protection Act, 2023 (\"DPDP Act\") and applicable rules made thereunder (\"DPDP Rules\"), and acts as Data Controller for the purposes of other applicable data protection legislation, in respect of personal data processed through the Hacktonomy platform.",
      "Hacktonomy is CyberFrat's vendor neutral certification body and training platform specializing in Cybersecurity, including the flagship Hacktonomy Certified AI Security Professional (HCASP) certification.",
      <>
        This Privacy Policy governs the collection, processing, disclosure,
        and protection of personal data of Data Principals when they visit,
        browse, interact with, or make purchases on our website located at{" "}
        <a
          href="https://hacktonomy.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#16fcfb] hover:underline"
        >
          https://hacktonomy.com
        </a>
        .
      </>,
      "Important Scope & Liability Limitation: This Privacy Policy applies exclusively to the primary hacktonomy.com website, including certification details, study guide downloads, voucher purchases, and partner inquiries.",
      "The Hacktonomy Examination & Testing Portal is a distinct, isolated platform governed by separate Examination Policies, Non-Disclosure Agreements (NDAs), candidate conduct terms, and remote proctoring privacy disclosures, including video/audio monitoring and identity verification. If you access or attempt an examination on the testing portal, portal-specific privacy and proctoring terms will apply.",
    ],
  },

  {
    title: "2. Categories of Personal Data We Collect",
    body: [
      <strong className="text-white">
        2.1 Information You Voluntarily Provide
      </strong>,
      "Contact & Identity Data: Full name, personal or business email address, phone number, company/organization name, and job title provided when submitting contact forms, requesting study guides, or subscribing to updates.",
      "Transaction & Billing Data: Billing address, contact details, payment reference identifiers, voucher selection details (e.g., Standard International $299 USD, Standard India ₹10,100 INR + GST), and order history.",
      "Payment Processing Notice: Sensitive payment card details are collected and processed directly by PCI-DSS compliant third-party payment gateways such as Stripe and Razorpay. CyberFrat does not store or process raw credit/debit card numbers on its servers.",
      "Partner Eligibility Data: Dynamic authorization tokens, referral metadata, or single-use verification links generated via partner applications, such as the CyberFrat Mobile App, to validate eligibility for partner pricing.",
      <strong className="text-white">
        2.2 Technical & Usage Data Collected Automatically
      </strong>,
      "Device & Connection Data: IP address, browser type and version, operating system, device model, preferred language settings, referring website URL, pages visited, clickstream data, and timestamp metrics.",
      "Cookies & Telemetry Data: Essential cookies, local storage, web beacons, and performance tags used for site navigation, security, currency preference retention, and web analytics.",
    ],
  },

  {
    title: "3. Notice, Consent, and Grounds of Processing",
    body: [
      "In accordance with the DPDP Act and Rules, and other applicable frameworks (GDPR, CCPA/CPRA) where relevant to non-Indian Data Principals, CyberFrat processes personal data on the following grounds:",
    ],
    list: [
      "Consent: Where processing is based on consent, we provide a clear, itemized notice — either in English or a language specified in the Eighth Schedule to the Constitution — describing the personal data being collected and the specific purpose of processing, at or before the time of seeking consent.",
      "Consent is free, specific, informed, unconditional, and unambiguous, given through clear affirmative action, and is obtained separately for each distinct purpose, such as marketing communications, non-essential cookies, and newsletter updates.",
      "You may withdraw consent at any time, as easily as it was given, without affecting the lawfulness of processing carried out before withdrawal.",
      "Certain Legitimate Uses under DPDP Act, Section 7: including where the Data Principal has voluntarily provided personal data for a specified purpose, such as voucher purchase or study guide request, and has not indicated non-consent.",
      "Contractual Necessity: Processing voucher orders, issuing invoice receipts, fulfilling access to the Official HCASP Study Guide and Lab Manual, and delivering customer service.",
      "Compliance with Legal Obligations: Retaining transaction records to comply with statutory accounting, tax, anti-money laundering, and corporate audit obligations.",
    ],
    footer: (
      <>
        Where CyberFrat engages a Consent Manager registered with the Data
        Protection Board of India for managing consent across services, details
        will be published here and made available through the Consent Manager's
        platform.
      </>
    ),
  },

  {
    title: "4. How We Share & Disclose Personal Data",
    body: [
      "CyberFrat does not sell, rent, or trade Data Principal personal data to third parties for monetary or commercial consideration. We disclose data solely under the following conditions:",
    ],
    list: [
      "Data Processors: Trusted third-party vendors, including payment gateways, cloud hosting providers, transaction email service providers, and analytics partners, engaged under written contracts requiring them to process personal data only as instructed by CyberFrat and to implement appropriate security safeguards.",
      "Partner Discount Verification: When you utilize a dynamic authorization link, such as via the CyberFrat Mobile App, limited verification metadata is shared with the relevant CyberFrat group entity strictly to confirm active membership eligibility for partner rates, under an internal data-sharing arrangement.",
      "Legal & Regulatory Mandatory Disclosures: We may disclose personal data to the Data Protection Board of India, law enforcement, regulatory bodies, courts, or statutory authorities when mandated by applicable law, warrant, subpoena, or to enforce our legal rights and safeguard community safety.",
      "Corporate Restructuring: In the event of a merger, acquisition, joint venture, or asset sale, Data Principal personal data may be transferred to the acquiring entity subject to the commitments in this Privacy Policy.",
    ],
  },

  {
    title: "5. Cookies and Tracking Technologies",
    body: [
      "We use cookies and similar tracking technologies to provide essential functionality, improve your experience, understand website usage, and support relevant services.",
    ],
    list: [
      "Strictly Necessary — checkout, login, security. No consent needed.",
      "Functional — language and display preferences. Consent needed.",
      "Analytics — traffic and usage tracking. Consent needed.",
      "Marketing — retargeting and ad personalization. Consent needed.",
      "Consent: A cookie banner lets you Accept All, Reject All, or choose by category. You can withdraw consent anytime via \"Cookie Preferences\" in the footer.",
      "Minors: Analytics and Marketing cookies are switched off for any account flagged as a minor and cannot be turned on.",
      "Third Parties: Some cookies are set by payment gateways and analytics providers under their own policies.",
      "Browser Controls: You can block or delete cookies in your browser. This may affect checkout or login.",
      "Retention: Session cookies clear on browser close. Persistent cookies last 6–24 months or until consent is withdrawn.",
    ],
  },

  {
    title: "6. Cross-Border Transfer of Personal Data",
    body: [
      "As a global certification organization, CyberFrat serves candidates worldwide. Under the DPDP Act, cross-border transfer of personal data is permitted by default except to countries or territories restricted by the Central Government of India by notification.",
      "Your personal data may accordingly be transferred to, stored, or processed on servers located outside India, including in the United States and the European Union, subject to any such restrictions in force at the time of transfer.",
      "Where non-Indian data protection law also applies to a transfer, such as GDPR for EU-origin data, we additionally rely on recognized mechanisms such as Standard Contractual Clauses (SCCs) as applicable.",
    ],
  },

  {
    title: "7. Data Security & Storage Retention",
    body: [
      <strong className="text-white">7.1 Security Safeguards</strong>,
      'Consistent with the DPDP Act\'s requirement to implement "reasonable security safeguards," CyberFrat maintains appropriate technical and organizational measures to protect personal data.',
    ],
    list: [
      "End-to-end Transport Layer Security (TLS/HTTPS 1.3) encryption for all web sessions.",
      "Tokenized transaction handling for payment processing.",
      "Strict role-based access controls (RBAC) and data isolation.",
      "Breach detection and internal escalation procedures to enable notification to the Data Protection Board of India and affected Data Principals as required under the DPDP Act and Rules in the event of a personal data breach.",
    ],
    footer: (
      <>
        <strong className="text-white">7.2 Retention Schedule</strong>
        <br />
        <br />
        Personal data is retained only for as long as necessary for the
        specified purpose, or as required to comply with applicable law,
        whichever is longer. Where you withdraw consent or your purpose is no
        longer being served and retention is not otherwise mandated by law, we
        will erase your personal data and cause our processors to do the same
        within the timelines prescribed under the DPDP Rules.
        <br />
        <br />
        Financial and transaction records are retained in line with statutory
        requirements under applicable tax and corporate law, including Income
        Tax Act and Companies Act record-keeping requirements.
      </>
    ),
  },

  {
    title: "8. Data Principal Rights & Response Timelines",
    body: [
      "Subject to your jurisdiction, you have the following rights as a Data Principal under the DPDP Act, with equivalent rights under GDPR/CCPA where applicable:",
    ],
    list: [
      "Right to Access Information: Obtain a summary of personal data being processed and the processing activities undertaken.",
      "Right to Correction and Erasure: Request correction of inaccurate or incomplete personal data, updating of personal data, and erasure of personal data that is no longer necessary for the purpose for which it was collected.",
      "Right to Grievance Redressal: Register a grievance with CyberFrat's Grievance Officer and receive a response within the prescribed timeline, before escalating to the Data Protection Board of India.",
      "Right to Nominate: Nominate another individual to exercise your rights under the DPDP Act in the event of your death or incapacity.",
      "Right to Withdraw Consent: Withdraw consent at any time, as easily as it was given.",
    ],
    footer: (
      <>
        <strong className="text-white">Grievance Redressal Process</strong>
        <br />
        <br />
        To exercise your rights or raise a grievance, contact our Grievance
        Officer at{" "}
        <a
          href="mailto:grievance@hacktonomy.com"
          className="text-[#16fcfb] hover:underline"
        >
          grievance@hacktonomy.com
        </a>
        .
        <br />
        <br />
        Response Timeline: We will acknowledge receipt within 5 business days
        and respond within the prescribed timeline in accordance with DPDP
        Rules requirements. Identity verification will be required prior to
        processing requests.
        <br />
        <br />
        If you are not satisfied with our response, you may escalate your
        grievance to the Data Protection Board of India.
        <br />
        <br />
        If you reside in the European Union or United Kingdom, you also have
        the right to lodge a complaint with your local Data Protection
        Authority.
      </>
    ),
  },

  {
    title: "9. Third-Party Links & Examination Portal Demarcation",
    body: [
      "Our website may contain links to external third-party sites or partner mobile applications, such as the CyberFrat App. CyberFrat is not responsible for the privacy practices, privacy statements, or terms of third-party platforms not operated by us.",
      "When candidates navigate from hacktonomy.com to the official Hacktonomy Examination Portal, they enter a secure testing environment subject to separate Portal Privacy Policies, candidate NDAs, and remote proctoring terms.",
      "Proctoring data, such as live webcam/microphone streams, identity verification documents, and anti-cheat telemetry, is governed exclusively by portal terms and is never collected or processed on this website.",
    ],
  },

  {
    title: "10. Processing of Children's Personal Data",
    body: [
      'Definition: A "child" under the DPDP Act is anyone under 18.',
    ],
    list: [
      "Age Check: Users declare their date of birth at signup. Under-18 accounts are flagged and blocked from onboarding until we get verifiable consent from a parent or guardian. Self-declared guardian consent is not enough.",
      "What's Restricted for Minor Accounts: No behavioral tracking beyond what is needed to deliver the service. No targeted ads. No analytics or marketing cookies.",
      "Data Collection Limited: Data collection is limited to account setup, certification delivery, and legal recordkeeping.",
      "No Consent, No Data: If we find a minor's data was collected without valid guardian consent, we stop processing and delete it, unless law requires us to keep it, such as tax records.",
      "Guardian Rights: A consenting parent or guardian can exercise all applicable Section 8 rights, including access, correction, erasure, grievance, and withdrawal for the child.",
    ],
  },

  {
    title: "11. Policy Revisions & Updates",
    body: [
      "We reserve the right to amend this Privacy Policy at any time to reflect changes in our operational procedures, technological advancements, or regulatory requirements.",
      'Material revisions will be posted on this page with an updated "Last Updated" date, and registered users will be notified by email where the change materially affects how their personal data is processed.',
      "We encourage visitors to review this policy periodically.",
    ],
  },

  {
    title: "12. Contact Information & Complaints",
    body: [
      // <>
      //   <strong className="text-white">Grievance Officer:</strong> [Name] —{" "}
      //   <a
      //     href="mailto:grievance@hacktonomy.com"
      //     className="text-[#16fcfb] hover:underline"
      //   >
      //     grievance@hacktonomy.com
      //   </a>
      // </>,
      // <>
      //   <strong className="text-white">
      //     Data Protection Officer / Privacy Queries:
      //   </strong>{" "}
      //   <a
      //     href="mailto:privacy@hacktonomy.com"
      //     className="text-[#16fcfb] hover:underline"
      //   >
      //     privacy@hacktonomy.com
      //   </a>
      // </>,
      <>
        <strong className="text-white">Candidate Support:</strong>{" "}
        <a
          href="mailto:support@hacktonomy.com"
          className="text-[#16fcfb] hover:underline"
        >
          support@hacktonomy.com
        </a>
      </>,
      <>
        <strong className="text-white">Registered Entity:</strong> CyberFrat
        Private Limited, F-203, Eastern Business District, Lal Bahadur Shastri
        Rd, Mumbai, Maharashtra 400078
      </>,
      <>
        <strong className="text-white">Official Website:</strong>{" "}
        <a
          href="https://hacktonomy.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#16fcfb] hover:underline"
        >
          https://hacktonomy.com/
        </a>
      </>,
      "Complaints regarding processing of personal data may be escalated to the Data Protection Board of India. EU/UK residents may additionally lodge a complaint with their local Data Protection Authority.",
    ],
  },
];

function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#07070c] text-white font-body">
      <Nav />

      <div className="relative px-6 md:px-12 py-20 md:py-28 noise overflow-hidden">
        {/* Background grid */}
        <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" />

        {/* Background glow */}
        <div className="absolute -top-40 right-0 h-[420px] w-[420px] rounded-full blur-[130px] opacity-30 bg-[#4d4479] pointer-events-none" />

        <div className="relative z-10 max-w-4xl mx-auto">
          {/* Header */}
          <div>
            <span className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#16fcfb]">
              Legal
            </span>

            <h1 className="mt-4 font-display font-700 text-4xl md:text-5xl tracking-tight text-white">
              Privacy Policy
            </h1>

            <p className="mt-4 font-body text-sm text-white/55">
              Privacy Policy for Hacktonomy.com — how CyberFrat collects, uses,
              processes, stores, and protects your personal data.
            </p>

            {/* <div className="mt-5 flex flex-col sm:flex-row gap-2 sm:gap-6 text-xs text-white/40 font-mono2">
              <span>
                Effective Date: <span className="text-white/60">[Date]</span>
              </span>

              <span>
                Last Updated:{" "}
                <span className="text-white/60">[Date]</span>
              </span>
            </div> */}
          </div>

          {/* Sections */}
          <div className="mt-12 space-y-10">
            {SECTIONS.map((section) => (
              <section
                key={section.title}
                className="rounded-2xl border border-[#3d414f] bg-[#0a0a12]/80 backdrop-blur-xl p-6 md:p-8"
              >
                <h2 className="font-display font-700 text-xl md:text-2xl text-white">
                  {section.title}
                </h2>

                {/* Body */}
                {section.body?.map((paragraph, index) => (
                  <p
                    key={index}
                    className="mt-4 font-body text-sm text-white/70 leading-relaxed"
                  >
                    {paragraph}
                  </p>
                ))}

                {/* List */}
                {section.list && (
                  <ul className="mt-5 space-y-3">
                    {section.list.map((item, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-3"
                      >
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#16fcfb] shrink-0" />

                        <span className="font-body text-sm text-white/75 leading-relaxed">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Footer */}
                {section.footer && (
                  <p className="mt-5 font-body text-sm text-white/70 leading-relaxed">
                    {section.footer}
                  </p>
                )}
              </section>
            ))}
          </div>

          {/* Bottom note */}
          <div className="mt-10 text-center">
            <p className="text-xs text-white/35 font-body">
              © {new Date().getFullYear()} CyberFrat Private Limited. All
              rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;

